<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKanbanAttribute } from 'dashboard/composables/useKanbanAttribute';
import { UNASSIGNED_KANBAN_COLUMN_VALUE } from 'dashboard/constants/kanban';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KanbanBoard from 'dashboard/components-next/Contacts/Kanban/KanbanBoard.vue';
import KanbanEmptyState from 'dashboard/components-next/Contacts/Kanban/KanbanEmptyState.vue';
import KanbanSettingsPopover from 'dashboard/components-next/Contacts/Kanban/KanbanSettingsPopover.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { uiSettings, updateUISettings } = useUISettings();

const uiFlags = useMapGetter('contactsKanban/getUIFlags');

const {
  listAttributes,
  hasKanbanAttribute: hasListAttributes,
  kanbanAttributeKey: groupByAttributeKey,
  kanbanAttribute: activeAttribute,
} = useKanbanAttribute();

const columnOrder = computed(() => {
  const values = [
    ...(activeAttribute.value?.attributeValues || []),
    UNASSIGNED_KANBAN_COLUMN_VALUE,
  ];
  const stored = uiSettings.value?.contacts_kanban_column_order;
  if (!stored) return values;
  const known = stored.filter(value => values.includes(value));
  const missing = values.filter(value => !known.includes(value));
  return [...known, ...missing];
});

const columnLabel = value =>
  value === UNASSIGNED_KANBAN_COLUMN_VALUE
    ? t('CONTACTS_LAYOUT.KANBAN.UNASSIGNED_COLUMN_LABEL')
    : value;

const columns = computed(() =>
  columnOrder.value.map(value => {
    const column = store.getters['contactsKanban/getColumn'](value);
    return {
      value,
      label: columnLabel(value),
      contacts: column.contacts,
      meta: column.meta,
      isFetching: column.isFetching,
      isFetchingMore: column.isFetchingMore,
    };
  })
);

const fetchBoard = () => {
  if (!groupByAttributeKey.value || !columnOrder.value.length) return;
  store.dispatch('contactsKanban/fetchColumns', {
    attributeKey: groupByAttributeKey.value,
    columnValues: columnOrder.value,
  });
};

const handleGroupByChange = attributeKey => {
  updateUISettings({ contacts_kanban_group_by_attribute_key: attributeKey });
};

const handleColumnOrderChange = order => {
  updateUISettings({ contacts_kanban_column_order: order });
};

const handleLoadMore = columnValue => {
  store.dispatch('contactsKanban/loadMoreColumn', {
    attributeKey: groupByAttributeKey.value,
    columnValue,
  });
};

const handleMoveCard = async ({
  contactId,
  fromColumnValue,
  toColumnValue,
}) => {
  try {
    await store.dispatch('contactsKanban/moveContact', {
      contactId,
      fromColumnValue,
      toColumnValue,
      attributeKey: groupByAttributeKey.value,
    });
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.KANBAN.MOVE_ERROR'));
  }
};

const handleCardClick = contactId => {
  router.push({ name: 'contacts_edit', params: { contactId } });
};

const goToListView = () => router.push({ name: 'contacts_dashboard_index' });

watch(groupByAttributeKey, fetchBoard);

onMounted(fetchBoard);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1">
    <header class="sticky top-0 z-20 px-6">
      <div class="flex items-center justify-between w-full py-6 gap-2 mx-auto">
        <div class="flex items-center gap-2">
          <Button
            v-tooltip.bottom="t('CONTACTS_LAYOUT.KANBAN.BACK_TO_LIST')"
            icon="i-lucide-arrow-left"
            color="slate"
            size="sm"
            variant="ghost"
            @click="goToListView"
          />
          <span class="text-xl font-medium truncate text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.KANBAN.TITLE') }}
          </span>
        </div>
        <KanbanSettingsPopover
          v-if="hasListAttributes"
          :list-attributes="listAttributes"
          :group-by-attribute-key="groupByAttributeKey"
          :column-order="columnOrder"
          @update:group-by="handleGroupByChange"
          @update:column-order="handleColumnOrderChange"
        />
      </div>
    </header>

    <main class="flex-1 px-6 pb-6 overflow-hidden">
      <KanbanEmptyState v-if="!hasListAttributes" />
      <div
        v-else-if="
          uiFlags.isFetchingColumns && !columns.some(c => c.contacts.length)
        "
        class="flex items-center justify-center w-full h-full"
      >
        <Spinner />
      </div>
      <KanbanBoard
        v-else
        :columns="columns"
        @load-more="handleLoadMore"
        @move-card="handleMoveCard"
        @card-click="handleCardClick"
      />
    </main>
  </div>
</template>
